import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import BinaryStateButton from "components/BinaryStateButton";
import ElementLoader from "components/ElementLoader";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { ProvisioningStateData } from "components/ProvisioningStateDot/ProvisioningStateDot";
import Tooltip from "components/Tooltip/Tooltip";
import { ActivityConfig, FeConfigType, RelationshipConfig, ResourceConfig } from "config/config";
import { getIsReadonly } from "config/resourceUtils";
import { getUserData } from "containers/Auth/Auth.selectors";
import { getActiveOrDefaultProjectId } from "containers/Projects/Projects.selectors";
import { ActionSignature } from "containers/Resources/components/CommonActions/Action";
import {
    clearDeleteActionSignature,
    clearRestoreActionSignature,
    clearTrashActionSignature,
    undeleteActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import { UncommittedDataGroupedByName } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { isEmpty } from "lodash";
import { MutableRefObject, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "./HiddenActionsComponent.scss";

interface HiddenActionsComponentProps {
    hiddenActionList?: string[];
    pristine?: boolean;
    resourceData: any;
    resourceName: FeConfigType;
    resourceConfig: ResourceConfig | RelationshipConfig | ActivityConfig;
    fetchResourceData: () => any;
    setModalVisible?: (isModalVisible: boolean) => void;
    applyAction?: MutableRefObject<(() => void) | undefined>;
    hasTooltip?: boolean;
    afterAction?: () => any | null;
    uncommittedDataByPath?: UncommittedDataGroupedByName;
    provisioningState?: ProvisioningStateData;
    popoverClassName?: string;
    iri?: string;
}

/**
 * HiddenActionsComponent renders record actions in a three-dot menu (ellipsis menu).
 *
 * This component handles:
 * - Displaying resource-specific actions in a dropdown menu
 * - Managing action visibility based on resource state and permissions
 * - Handling action execution with proper loading states
 * - Coordinating with parent components for data refresh after actions
 *
 * @param pristine - Whether the form is pristine (has no changes)
 * @param resourceData - The resource data for action context
 * @param resourceName - The name of the resource type
 * @param resourceConfig - Configuration defining current resource type
 * @param afterAction - Callback to execute after action completion
 * @param fetchResourceData - Function to refresh resource data
 * @param setModalVisible - Function to control modal visibility
 * @param applyAction - Reference to store action function
 * @param hiddenActionList - List of action keys to exclude from display
 */

export const HiddenActionsComponent = ({
    pristine,
    resourceData,
    resourceName,
    resourceConfig,
    fetchResourceData,
    setModalVisible,
    applyAction,
    hasTooltip,
    afterAction,
    hiddenActionList,
    uncommittedDataByPath = {},
    provisioningState,
    popoverClassName,
    iri,
}: HiddenActionsComponentProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const activeProjectId = useSelector(getActiveOrDefaultProjectId);
    const userData = useSelector(getUserData);
    const isReadOnly = getIsReadonly(resourceConfig, resourceData);

    const [hiddenActionsPopoverEl, setHiddenActionsPopoverEl] = useState(null);
    const [isActionInFlight, setActionInFlight] = useState(false);
    const hiddenResourceActions = resourceConfig?.actions?.hidden.reduce(
        (acc: ActionSignature[], action: ActionSignature) => {
            const value =
                action?.key === "delete" && resourceData?.deleted_at
                    ? [undeleteActionSignature]
                    : [action];
            return [...acc, ...value];
        },
        [clearTrashActionSignature, clearRestoreActionSignature, clearDeleteActionSignature]
    );

    const onApplyAction = (hiddenAction, actionData) => {
        setActionInFlight(true);
        Promise.resolve(hiddenAction.action(actionData, dispatch, history))
            .then(() =>
                hiddenAction?.key === "undelete" || hiddenAction?.key === "delete"
                    ? fetchResourceData()
                    : null
            )
            .finally(() => setActionInFlight(false));
    };

    const actions = (
        <>
            <div className="hiddenActionsButtonWrapper">
                <BinaryStateButton
                    trueStateIcon={IconCollection.dotsVertical}
                    falseStateIcon={IconCollection.dotsVertical}
                    trueStateColor="primary"
                    state={Boolean(hiddenActionsPopoverEl)}
                    onClick={(event) => {
                        setHiddenActionsPopoverEl(event.target);
                    }}
                    className="hiddenActionsButton"
                    disabled={isEmpty(resourceData) || isActionInFlight}
                />
                {isActionInFlight && (
                    <div className="actionButtonLoader">
                        <ElementLoader />
                    </div>
                )}
            </div>
            <Menu
                open={Boolean(hiddenActionsPopoverEl)}
                anchorEl={hiddenActionsPopoverEl}
                onClose={() => {
                    setHiddenActionsPopoverEl(null);
                }}
                data-testid="menuList"
                className={popoverClassName}
            >
                {hiddenResourceActions?.map((hiddenAction) => {
                    const actionData = {
                        resourceType: resourceName,
                        resourceData,
                        iri,
                        activeProjectId,
                        userData,
                        isReadOnly,
                        afterAction: hiddenAction.hasAfterAction ? afterAction : undefined,
                        isHidden: hiddenActionList?.includes(hiddenAction?.key),
                        uncommittedData: uncommittedDataByPath,
                        provisioningState,
                    };

                    const onApplyCurrentAction = () => onApplyAction(hiddenAction, actionData);

                    if (hiddenAction?.omitWhen && hiddenAction.omitWhen(actionData)) return null;
                    return (
                        <MenuItem
                            key={`hiddenAction${hiddenAction?.key}`}
                            onClick={() => {
                                setHiddenActionsPopoverEl(null);
                                if (pristine) {
                                    onApplyCurrentAction();
                                }
                                // show blockingNavigationModal
                                else if (setModalVisible && applyAction) {
                                    setModalVisible(true);
                                    applyAction.current = onApplyCurrentAction;
                                }
                            }}
                            disabled={
                                isActionInFlight ||
                                (hiddenAction?.disableWhen && hiddenAction.disableWhen(actionData))
                            }
                        >
                            <ListItemIcon>
                                <Icon icon={hiddenAction?.icon(actionData)} />
                            </ListItemIcon>
                            <ListItemText>{hiddenAction?.label(actionData)}</ListItemText>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );

    const actionsPopover = hasTooltip ? <Tooltip overlay="More">{actions}</Tooltip> : actions;
    return hiddenResourceActions ? actionsPopover : null;
};
